<script setup lang="ts">
/**
 * A file shown whole inside a screen (#1734), such as the body of a chat's preview dialog: an image drawn in full,
 * or a PDF in the browser's own viewer, framed. Either is as wide as its container and sized to the dynamic
 * viewport, so it stays clear of a phone browser's chrome.
 *
 * The preview fetches nothing itself: the screen owns where the bytes come from and hands in their source, such as
 * an object URL of the file's own bytes, typed as its own kind.
 */
defineProps<{
    /** The file's bytes, as a URL the browser can show. */
    src: string
    /** The file's full name: the image's alternative text, or the frame's title (`Preview of brief.pdf`). */
    filename: string
    /** Whether the file is an image, drawn in full; anything else is shown in a frame, as a browser shows a PDF. */
    image: boolean
}>()
</script>

<template>
    <img v-if="image" class="zh-file-preview zh-file-preview--image" :src="src" :alt="filename" />
    <iframe v-else class="zh-file-preview zh-file-preview--frame" :src="src" :title="`Preview of ${filename}`" />
</template>

<style scoped>
.zh-file-preview {
    display: block;
    inline-size: 100%;
}

/* The whole picture, never cropped: as tall as it is, up to most of the screen. */
.zh-file-preview--image {
    max-block-size: 70dvh;
    object-fit: contain;
}

/* The browser's own PDF viewer, given most of the screen to page through. */
.zh-file-preview--frame {
    block-size: 70dvh;
    border: 1px solid var(--line);
}
</style>
